import React, { useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Play,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  Scale,
  Ship,
  FileText,
  Search,
  Radio,
  Zap,
  ChevronRight,
  Eye,
  Layers,
  Lock,
  Unlock,
  Check,
  Award,
  ShieldCheck,
  Clock,
  Briefcase,
  HelpCircle,
  FileCheck2,
  Printer
} from 'lucide-react'
import toast from 'react-hot-toast'
import { ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  expediente?: ExpedienteDespacho | null
  todosExpedientes: ExpedienteDespacho[]
  onSeleccionarExpediente: (exp: ExpedienteDespacho) => void
  onAbrirHojaFisica: (tipoDoc: 'PEDIMENTO' | 'BL' | 'TARJA' | 'PIS' | 'VGM' | 'FACTURA' | 'DODA' | 'MVE') => void
  onActualizarExpediente: (expActualizado: ExpedienteDespacho) => void
}

type EtapaSimulacion = 1 | 2 | 3 | 4 | 5

interface LogTerminal {
  id: string
  hora: string
  modulo: string
  mensaje: string
  tipo: 'INFO' | 'SUCCESS' | 'WARN' | 'DANGER'
}

interface ItemSOP {
  id: string
  estacion: EtapaSimulacion
  titulo: string
  detalle: string
  fundamento: string
  multaEvitadaMXN: number
  demoraEvitadaUSD: number
}

// Catálogo de Procedimientos Operativos Estándar (SOP Oficial de Vida Real)
const REGLAS_SOP: ItemSOP[] = [
  // Estación 1: Revalidación Naviera
  {
    id: 'sop-1-1',
    estacion: 1,
    titulo: 'Liberación de Flete Marítimo y Garantías',
    detalle: 'Confirmar pago de Ocean Freight y depósito en garantía de contenedor en portal de la naviera.',
    fundamento: 'Art. 590 Código de Comercio & Reglas Navieras INCOTERMS 2020',
    multaEvitadaMXN: 0,
    demoraEvitadaUSD: 180
  },
  {
    id: 'sop-1-2',
    estacion: 1,
    titulo: 'Cotejo de Consignee vs RFC del Importador',
    detalle: 'Validar que el consignatario del B/L coincida exactamente con la Factura Comercial y el Pedimento.',
    fundamento: 'Art. 36-A Frac. I Ley Aduanera (Sanción por datos inexactos)',
    multaEvitadaMXN: 18450,
    demoraEvitadaUSD: 0
  },
  {
    id: 'sop-1-3',
    estacion: 1,
    titulo: 'Vigencia de Carta Encomienda y Padrón Activo',
    detalle: 'Verificar en portal SAT que el importador no tenga suspensión de padrón y la patente esté autorizada.',
    fundamento: 'Art. 59 Frac. IV Ley Aduanera & Regla 1.2.6 RGCE',
    multaEvitadaMXN: 24500,
    demoraEvitadaUSD: 0
  },

  // Estación 2: Garita PIS & Báscula BP1
  {
    id: 'sop-2-1',
    estacion: 2,
    titulo: 'Autenticación Cita ASIPONA PIS & CAAT',
    detalle: 'Escanear QR de acceso. Cotejar placas del tractocamión y gafete único del chofer transportista.',
    fundamento: 'Regla 1.9.19 RGCE & Manual de Operación ASIPONA Manzanillo',
    multaEvitadaMXN: 12800,
    demoraEvitadaUSD: 0
  },
  {
    id: 'sop-2-2',
    estacion: 2,
    titulo: 'Certificación VGM bajo Convenio SOLAS (±500 kg)',
    detalle: 'Comprobar en báscula BP1 que el peso vehicular neto no discrepe más de 500 kg del VGM declarado.',
    fundamento: 'Convenio SOLAS Cap. VI Regla 2 (Enmienda OMI) & Circular Puertos 04/2016',
    multaEvitadaMXN: 35000,
    demoraEvitadaUSD: 350
  },
  {
    id: 'sop-2-3',
    estacion: 2,
    titulo: 'Inspección Estructural de 7 Puntos C-TPAT',
    detalle: 'Inspección de chasis, esquineros, vigas, techo y cerraduras para descartar compartimentos ocultos.',
    fundamento: 'Estándar Internacional C-TPAT / OEA Seguridad en Cadena Logística',
    multaEvitadaMXN: 15000,
    demoraEvitadaUSD: 0
  },

  // Estación 3: Previo Ocular & Sellos
  {
    id: 'sop-3-1',
    estacion: 3,
    titulo: 'Verificación de Precinto de Alta Seguridad ISO 17712',
    detalle: 'Comprobar numeración del sello tipo perno contra el B/L y verificar que no presente signos de corte.',
    fundamento: 'Norma ISO/PAS 17712 & Art. 186 Frac. II Ley Aduanera',
    multaEvitadaMXN: 22900,
    demoraEvitadaUSD: 0
  },
  {
    id: 'sop-3-2',
    estacion: 3,
    titulo: 'Muestreo Físico vs Packing List & Factura',
    detalle: 'Apertura de contenedor en muelle, conteo aleatorio de bultos y validación de marcas comerciales.',
    fundamento: 'Art. 42 Ley Aduanera (Examen Previo de Mercancías)',
    multaEvitadaMXN: 28500,
    demoraEvitadaUSD: 0
  },
  {
    id: 'sop-3-3',
    estacion: 3,
    titulo: 'Sellado Fitosanitario en Pallets (NOM-144 / NIMF-15)',
    detalle: 'Asegurar que los embalajes de madera porten el sello térmico internacional IPPC para evitar plagas.',
    fundamento: 'NOM-144-SEMARNAT & Regulación Fitosanitaria Internacional NIMF-15',
    multaEvitadaMXN: 18000,
    demoraEvitadaUSD: 200
  },

  // Estación 4: Semáforo Fiscal SAT
  {
    id: 'sop-4-1',
    estacion: 4,
    titulo: 'Lectura de Código Bidimensional DODA / PITA',
    detalle: 'Presentar el código fiscal en lector óptico de balanza automatizada ante la autoridad aduanera.',
    fundamento: 'Art. 43 Ley Aduanera (Mecanismo de Selección Automatizada)',
    multaEvitadaMXN: 0,
    demoraEvitadaUSD: 0
  },
  {
    id: 'sop-4-2',
    estacion: 4,
    titulo: 'Auditoría de Alertas SICREFIS y Balanza de Saldos',
    detalle: 'Asegurar que el pedimento no arroje error SICREFIS [122] (sin saldo en balanza de recinto fiscalizado).',
    fundamento: 'Manual Técnico de Operación SAAI-M3 / SICREFIS SAT',
    multaEvitadaMXN: 16500,
    demoraEvitadaUSD: 180
  },
  {
    id: 'sop-4-3',
    estacion: 4,
    titulo: 'Protocolo de Reconocimiento Rojo / Rayos Gamma',
    detalle: 'En caso de semáforo rojo, encauzar a andén de inspección no intrusiva con acta de inicio.',
    fundamento: 'Art. 44 y 46 Ley Aduanera (Reconocimiento Físico y Documental)',
    multaEvitadaMXN: 42000,
    demoraEvitadaUSD: 0
  },

  // Estación 5: Tarja y Salida (Gate-Out)
  {
    id: 'sop-5-1',
    estacion: 5,
    titulo: 'Cotejo Cruzado de Tarja SSA vs B/L y Pedimento',
    detalle: 'Verificar que la tarja física emitida por la grúa pórtico refleje los bultos reales y sellos finales.',
    fundamento: 'Reglas de Operación del Puerto de Manzanillo / SSA México',
    multaEvitadaMXN: 14000,
    demoraEvitadaUSD: 0
  },
  {
    id: 'sop-5-2',
    estacion: 5,
    titulo: 'Levantamiento de Acta de Hechos ante Faltantes',
    detalle: 'Si existe merma o daño en maniobra, asentar constancia escrita antes del cruce de garita.',
    fundamento: 'Art. 60 Ley Aduanera (Responsabilidad Solidaria de la Terminal)',
    multaEvitadaMXN: 31000,
    demoraEvitadaUSD: 0
  },
  {
    id: 'sop-5-3',
    estacion: 5,
    titulo: 'Firma Electrónica y Delimitación de Responsabilidad',
    detalle: 'El alumno receptor firma digitalmente la Tarja de Internación. Concluye el ciclo logístico formal.',
    fundamento: 'Art. 89 Código de Comercio (Firma Electrónica) & Tarja SSA',
    multaEvitadaMXN: 10000,
    demoraEvitadaUSD: 0
  }
]

export const SimuladorDespachoEnVivo: React.FC<Props> = ({
  expediente,
  todosExpedientes,
  onSeleccionarExpediente,
  onAbrirHojaFisica,
  onActualizarExpediente
}) => {
  // Etapa actual de la simulación
  const [etapa, setEtapa] = useState<EtapaSimulacion>(1)

  // Estado del Simulador
  const [simulando, setSimulando] = useState<boolean>(false)
  const [velocidad, setVelocidad] = useState<'1x' | '2x'>('1x')
  const [modoReto, setModoReto] = useState<boolean>(false)
  const [tipoReto, setTipoReto] = useState<'SOBREPESO_BASCULA' | 'SELLO_VIOLADO' | 'SEMAFORO_ROJO'>('SEMAFORO_ROJO')

  // Checklist de Turno Laboral (IDs de items verificados por el alumno)
  const [checksRealizados, setChecksRealizados] = useState<string[]>([
    'sop-1-1' // Uno verificado por defecto para orientar al alumno
  ])

  // Estados interactivos por etapa
  // Etapa 1
  const [blRevalidado, setBlRevalidado] = useState<boolean>(true)
  // Etapa 2
  const [pesajeCompletado, setPesajeCompletado] = useState<boolean>(false)
  const [pesoDisplay, setPesoDisplay] = useState<number>(0)
  const [toleranciaOk, setToleranciaOk] = useState<boolean>(true)
  // Etapa 3
  const [selloVerificado, setSelloVerificado] = useState<boolean>(true)
  const [previoRealizado, setPrevioRealizado] = useState<boolean>(false)
  // Etapa 4
  const [semaforoPulsado, setSemaforoPulsado] = useState<boolean>(false)
  const [resultadoSemaforo, setResultadoSemaforo] = useState<'VERDE' | 'ROJO' | null>(null)
  // Etapa 5
  const [tarjaFirmada, setTarjaFirmada] = useState<boolean>(false)
  const [modalDictamenAbierto, setModalDictamenAbierto] = useState<boolean>(false)

  // Bitácora de Eventos de la Terminal en tiempo real (TOS Live Log)
  const [logs, setLogs] = useState<LogTerminal[]>([
    {
      id: 'log-1',
      hora: '14:15:00',
      modulo: 'ASIPONA',
      mensaje: `Cita PIS programada para contenedor ${expediente?.contenedor?.numero || 'S/N'}. Ventana activa.`,
      tipo: 'INFO'
    },
    {
      id: 'log-2',
      hora: '14:16:10',
      modulo: 'NAVIERA',
      mensaje: `Booking BKG-ZLO confirmado por naviera ${expediente?.contenedor?.lineaNaviera || 'NAVIERA'}.`,
      tipo: 'INFO'
    }
  ])

  // Agregar log a la bitácora
  const agregarLog = (modulo: string, mensaje: string, tipo: 'INFO' | 'SUCCESS' | 'WARN' | 'DANGER' = 'INFO') => {
    const ahora = new Date().toTimeString().slice(0, 8)
    setLogs((prev) => [
      {
        id: `log-${Date.now()}-${Math.random()}`,
        hora: ahora,
        modulo,
        mensaje,
        tipo
      },
      ...prev.slice(0, 19)
    ])
  }

  // Toggle de un check en el SOP Checklist
  const handleToggleCheck = (item: ItemSOP) => {
    const yaMarcado = checksRealizados.includes(item.id)
    if (yaMarcado) {
      setChecksRealizados((prev) => prev.filter((id) => id !== item.id))
      agregarLog('AUDITORÍA SOP', `Control desmarcado: ${item.titulo}`, 'WARN')
    } else {
      setChecksRealizados((prev) => [...prev, item.id])
      const premio = item.multaEvitadaMXN > 0
        ? `+$${item.multaEvitadaMXN.toLocaleString()} MXN multas evitadas`
        : `+$${item.demoraEvitadaUSD} USD demoras evitadas`
      agregarLog('AUDITORÍA SOP', `Punto verificado: ${item.titulo} (${premio})`, 'SUCCESS')
      toast.success(`✓ Verificado: ${item.titulo}`)
    }
  }

  // Cálculo de KPIs laborales del turno
  const metricasTurno = useMemo(() => {
    let multas = 0
    let demoras = 0

    REGLAS_SOP.forEach((item) => {
      if (checksRealizados.includes(item.id)) {
        multas += item.multaEvitadaMXN
        demoras += item.demoraEvitadaUSD
      }
    })

    const totalItems = REGLAS_SOP.length
    const completados = checksRealizados.length
    const precision = Math.round((completados / totalItems) * 100)

    let nivelCompetencia = 'Cadete en Capacitación'
    if (precision >= 90) nivelCompetencia = 'Director de Despacho Senior'
    else if (precision >= 60) nivelCompetencia = 'Inspector Aduanal Certificado'
    else if (precision >= 30) nivelCompetencia = 'Operador Portuario Junior'

    return {
      multas,
      demoras,
      precision,
      completados,
      totalItems,
      nivelCompetencia
    }
  }, [checksRealizados])

  // Datos del Puesto de Trabajo según la estación activa
  const puestoLaboral = useMemo(() => {
    switch (etapa) {
      case 1:
        return {
          titulo: 'Ejecutivo de Tráfico & Revalidación Naviera',
          gafete: 'OPERADOR FORWARDER',
          entidad: `Agencia de Carga & Línea ${expediente?.contenedor?.lineaNaviera || 'Naviera'}`,
          mision: 'Liberar fletes marítimos, validar garantías de contenedor y endosar el B/L Master.',
          color: 'from-blue-600 to-indigo-700',
          borde: 'border-blue-500/30',
          textoColor: 'text-blue-400'
        }
      case 2:
        return {
          titulo: 'Operador de Garita & Báscula BP1',
          gafete: 'BÁSCULA FISCAL TOS',
          entidad: 'ASIPONA Manzanillo / SSA Marine México (TEC I)',
          mision: 'Verificar cita PIS de acceso, pesar conjunto tracto-contenedor y certificar VGM bajo SOLAS.',
          color: 'from-emerald-600 to-teal-700',
          borde: 'border-emerald-500/30',
          textoColor: 'text-emerald-400'
        }
      case 3:
        return {
          titulo: 'Inspector de Patio & Previo Ocular',
          gafete: 'INSPECTOR C-TPAT / OEA',
          entidad: 'Agencia Aduanal & Recinto Fiscalizado',
          mision: 'Cotejar integridad de sellos perimetrales ISO 17712, contar bultos y revisar madera NOM-144.',
          color: 'from-purple-600 to-fuchsia-700',
          borde: 'border-purple-500/30',
          textoColor: 'text-purple-400'
        }
      case 4:
        return {
          titulo: 'Oficial de Selección Automatizada (SAT / ANAM)',
          gafete: 'AUTORIDAD ADUANERA',
          entidad: 'Agencia Nacional de Aduanas de México (Aduana 160 Manzanillo)',
          mision: 'Presentar código bidimensional DODA/PITA, auditar balanza SAAI y ejecutar selección fiscal.',
          color: 'from-amber-600 to-orange-700',
          borde: 'border-amber-500/30',
          textoColor: 'text-amber-400'
        }
      case 5:
        return {
          titulo: 'Auditor de Tarja de Internación & Receptor',
          gafete: 'CONSIGNATARIO FINAL',
          entidad: `Empresa Importadora (${expediente?.actores?.receptor?.nombre || 'Importador'})`,
          mision: 'Cotejar bultos contra Tarja de grúa pórtico SSA, delimitar responsabilidad y firmar internación.',
          color: 'from-teal-600 to-cyan-700',
          borde: 'border-teal-500/30',
          textoColor: 'text-teal-400'
        }
    }
  }, [etapa, expediente])

  // Peso meta para simular la báscula
  const pesoMeta = useMemo(() => {
    const base = (expediente?.contenedor?.pesoBrutoKg ?? 24850) + 13600 // Camión + Contenedor
    if (modoReto && tipoReto === 'SOBREPESO_BASCULA') {
      return base + 850 // Exceso de 850 kg (viola SOLAS)
    }
    return base
  }, [expediente, modoReto, tipoReto])

  // Resetear simulación
  const handleResetearSimulador = () => {
    setEtapa(1)
    setSimulando(false)
    setPesajeCompletado(false)
    setPesoDisplay(0)
    setToleranciaOk(true)
    setSelloVerificado(true)
    setPrevioRealizado(false)
    setSemaforoPulsado(false)
    setResultadoSemaforo(null)
    setTarjaFirmada(false)
    setChecksRealizados(['sop-1-1'])
    agregarLog('SIMULADOR', 'Turno laboral reiniciado a Estación 1 (Booking & Revalidación).', 'INFO')
    toast.success('Simulador de turno reiniciado.')
  }

  // Acción: Simular Báscula BP1
  const handleSimularPesaje = () => {
    setSimulando(true)
    agregarLog('BÁSCULA BP1', 'Tractocamión posicionado sobre plataforma 1. Iniciando pesaje dinámico...', 'INFO')

    let actual = 10000
    const paso = velocidad === '2x' ? 2500 : 1200
    const intervalo = setInterval(() => {
      actual += paso
      if (actual >= pesoMeta) {
        clearInterval(intervalo)
        setPesoDisplay(pesoMeta)
        setPesajeCompletado(true)
        setSimulando(false)

        const vgmReal = pesoMeta - 13600
        const vgmDeclarado = expediente?.contenedor?.vgmDeclaradoKg ?? 25000
        const diferencia = Math.abs(vgmReal - vgmDeclarado)
        const cumpleSolas = diferencia <= 500

        setToleranciaOk(cumpleSolas)

        if (cumpleSolas) {
          agregarLog(
            'BÁSCULA BP1',
            `Pesaje finalizado: ${pesoMeta.toLocaleString()} kg total. VGM Contenedor: ${vgmReal.toLocaleString()} kg. TOLERANCIA SOLAS CUMPLIDA (±${diferencia} kg).`,
            'SUCCESS'
          )
          toast.success('Báscula BP1: Pesaje exitoso conforme a Convenio SOLAS.')
        } else {
          agregarLog(
            'BÁSCULA BP1',
            `ALERTA SOLAS: Diferencia de ${diferencia} kg excede el límite reglamentario de 500 kg. Requiere rectificación de peso.`,
            'DANGER'
          )
          toast.error('Báscula BP1: Sobrepeso detectado excede tolerancia legal de 500 kg.')
        }
      } else {
        setPesoDisplay(actual)
      }
    }, 50)
  }

  // Acción: Pulsar Botón del Semáforo Fiscal SAT
  const handlePulsarSemaforo = () => {
    setSemaforoPulsado(true)
    agregarLog('ANAM SAT', 'Pedimento presentado ante lector óptico. Activando mecanismo de selección automatizada...', 'INFO')

    setTimeout(() => {
      const resultado = modoReto && tipoReto === 'SEMAFORO_ROJO' ? 'ROJO' : Math.random() > 0.25 ? 'VERDE' : 'ROJO'
      setResultadoSemaforo(resultado)

      if (resultado === 'VERDE') {
        agregarLog(
          'ANAM SAT',
          `DESADUANAMIENTO LIBRE (SEMÁFORO VERDE). El contenedor ${expediente?.contenedor?.numero || 'S/N'} puede continuar a entrega.`,
          'SUCCESS'
        )
        toast.success('¡Semáforo Verde! Desaduanamiento libre concedido.')
      } else {
        agregarLog(
          'ANAM SAT',
          `RECONOCIMIENTO ADUANERO (SEMÁFORO ROJO). La unidad debe dirigirse a la Bahía de Rayos Gamma e Inspección Intrusiva.`,
          'DANGER'
        )
        toast.error('Semáforo Rojo: Unidad sujeta a Reconocimiento Aduanero.')
      }
    }, 1200)
  }

  // Avanzar de etapa
  const avanzarEtapa = (siguiente: EtapaSimulacion) => {
    // Validar si los checks del SOP actual están completos
    const checksEtapaActual = REGLAS_SOP.filter((r) => r.estacion === etapa)
    const checksPendientes = checksEtapaActual.filter((r) => !checksRealizados.includes(r.id))

    if (checksPendientes.length > 0) {
      toast(`⚠️ Nota Operativa: Te faltan ${checksPendientes.length} puntos del SOP por auditar en este puesto.`, {
        icon: '📋'
      })
    }

    setEtapa(siguiente)
    const nombres = {
      1: 'Booking & Revalidación',
      2: 'Garita PIS & Báscula BP1',
      3: 'Patio de Maniobras & Previo',
      4: 'Semáforo Fiscal SAT',
      5: 'Gate-Out & Tarja Final'
    }
    agregarLog('CONTROL OPERATIVO', `Puesto cambiado a Etapa ${siguiente}: ${nombres[siguiente]}.`, 'INFO')
  }

  // Finalizar y marcar expediente como desaduanado o cerrado
  const handleFinalizarDespacho = () => {
    if (!expediente) return
    setTarjaFirmada(true)
    const expActualizado: ExpedienteDespacho = {
      ...expediente,
      estado: resultadoSemaforo === 'VERDE' ? 'CERRADO' : 'INCIDENCIA',
      tarjaInternacion: {
        firmadaPorReceptor: true,
        fechaFirma: new Date().toLocaleString('es-MX'),
        observacionesReceptor: `Despacho completado con ${metricasTurno.precision}% de apego al SOP laboral.`,
        resultado: resultadoSemaforo === 'VERDE' ? 'CONFORME' : 'DISCREPANCIA'
      }
    }
    onActualizarExpediente(expActualizado)
    agregarLog('TERMINAL SSA', `Turno laboral concluido. Tarja de internación firmada. Unidad liberada.`, 'SUCCESS')
    toast.success('¡Turno laboral completado con éxito!')
    setModalDictamenAbierto(true)
  }

  if (!expediente) {
    return (
      <div className="p-10 rounded-[24px] bg-white/80 dark:bg-[#0c0c0c] border border-slate-200/80 dark:border-white/[0.08] backdrop-blur-xl shadow-xl text-center max-w-xl mx-auto my-8 space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-500 flex items-center justify-center mx-auto">
          <Ship className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 dark:text-white">
          Simulador 1.0 en Vivo
        </h3>
        <p className="text-sm text-slate-600 dark:text-zinc-400">
          No hay un expediente seleccionado para simular en vivo. Selecciona un salón con expedientes o genera una práctica con células para 4-CAS-1.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6 select-none">
      {/* ========================================================================= */}
      {/* CENTRO DE MANDO / HUD DEL TURNO LABORAL REAL (WORKPLACE SIMULATION) */}
      {/* ========================================================================= */}
      <div className="p-5 sm:p-6 rounded-3xl bg-gradient-to-r from-slate-950 via-[#0e1017] to-slate-900 border border-slate-800 shadow-2xl relative overflow-hidden">
        {/* Glow de ambientación industrial */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5 relative z-10">
          {/* Título & Telemetría del Turno */}
          <div className="flex items-start gap-4">
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30 shrink-0">
              <Briefcase className="h-7 w-7" />
            </div>

            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/25 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-ping" />
                  Simulador 1.0 · Turno Laboral en Vivo · Puerto de Manzanillo
                </span>
                <span className="font-mono text-xs font-bold text-slate-400">
                  Terminal: {expediente.buque.terminal}
                </span>
              </div>

              <h2 className="text-lg sm:text-xl font-black text-white tracking-tight">
                Simulador 1.0 · Operaciones Portuarias & Despacho Aduanal
              </h2>

              <p className="text-xs text-slate-400 hidden sm:block">
                Entrénate en los 5 puestos reales: Tramitador, Inspector de Báscula, Verificador de Sellos, Oficial SAT y Receptor.
              </p>
            </div>
          </div>

          {/* Controles de Simulación (Velocidad, Retos, Reinicio) */}
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {/* Selector de Expediente */}
            <select
              aria-label="Seleccionar embarque activo"
              value={expediente.id}
              onChange={(e) => {
                const enc = todosExpedientes.find((x) => x.id === e.target.value)
                if (enc) onSeleccionarExpediente(enc)
              }}
              className="px-2.5 py-1.5 rounded-xl bg-slate-800/90 border border-slate-700 text-xs font-bold text-white focus:outline-none max-w-[170px] truncate"
            >
              {todosExpedientes.map((x) => (
                <option key={x.id} value={x.id} className="bg-slate-900">
                  {x.folio} - {x.contenedor.numero}
                </option>
              ))}
            </select>

            {/* Velocidad */}
            <button
              type="button"
              onClick={() => setVelocidad(velocidad === '1x' ? '2x' : '1x')}
              className="px-2.5 py-1.5 rounded-xl bg-slate-800 border border-slate-700 hover:border-slate-600 text-xs font-mono font-bold text-slate-300 transition-all"
              title="Velocidad de simulación"
            >
              Vel: <strong className="text-cyan-400">{velocidad}</strong>
            </button>

            {/* Toggle Modo Reto */}
            <button
              type="button"
              onClick={() => {
                const nuevoModo = !modoReto
                setModoReto(nuevoModo)
                if (nuevoModo) {
                  agregarLog('SIMULADOR', `Modo Reto activado con contingencia: ${tipoReto}.`, 'WARN')
                  toast('Modo Reto ACTIVADO: Se inyectarán incidencias operativas para resolver.', { icon: '⚠️' })
                } else {
                  agregarLog('SIMULADOR', 'Modo Normal restablecido: flujo estándar.', 'INFO')
                  toast('Modo Normal RESTABLECIDO: Despacho estándar conforme.', { icon: '✓' })
                }
              }}
              className={`px-2.5 py-1.5 rounded-xl text-xs font-bold border transition-all flex items-center gap-1.5 ${
                modoReto
                  ? 'bg-rose-500/20 text-rose-300 border-rose-500/40 shadow-sm shadow-rose-500/20'
                  : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'
              }`}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>{modoReto ? 'Reto: ON' : 'Modo Reto'}</span>
            </button>

            {modoReto && (
              <select
                aria-label="Seleccionar contingencia de entrenamiento"
                value={tipoReto}
                onChange={(e) => {
                  const val = e.target.value as 'SOBREPESO_BASCULA' | 'SELLO_VIOLADO' | 'SEMAFORO_ROJO'
                  setTipoReto(val)
                  agregarLog('SIMULADOR', `Incidencia de entrenamiento configurada: ${val}`, 'WARN')
                  toast(`Reto activo: ${val}`, { icon: '⚠️' })
                }}
                className="bg-slate-900 border border-rose-500/40 text-rose-300 text-xs rounded-xl px-2 py-1.5 outline-none font-mono focus:border-rose-400 max-w-[170px] truncate"
              >
                <option value="SEMAFORO_ROJO">Rojo SAT (Reconocimiento)</option>
                <option value="SOBREPESO_BASCULA">Sobrepeso en Báscula (&gt;500 kg)</option>
                <option value="SELLO_VIOLADO">Discrepancia en Sellos</option>
              </select>
            )}

            {/* Botón Reiniciar */}
            <button
              type="button"
              onClick={handleResetearSimulador}
              className="p-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-400 hover:text-white transition-all"
              title="Reiniciar turno laboral"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* BARRA DE IMPACTO ECONÓMICO Y LABORAL EN TIEMPO REAL (KPI METRICS) */}
        {/* ========================================================================= */}
        <div className="mt-4 pt-4 border-t border-slate-800/80 grid grid-cols-2 sm:grid-cols-4 gap-3 font-mono text-xs">
          <div className="p-3 rounded-2xl bg-slate-900/80 border border-emerald-500/30">
            <span className="text-[10px] text-slate-400 font-bold uppercase block flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
              Multas SAT Evitadas
            </span>
            <span className="text-base sm:text-lg font-black text-emerald-400">
              ${metricasTurno.multas.toLocaleString()} <span className="text-[10px] text-slate-400">MXN</span>
            </span>
          </div>

          <div className="p-3 rounded-2xl bg-slate-900/80 border border-blue-500/30">
            <span className="text-[10px] text-slate-400 font-bold uppercase block flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-blue-400" />
              Demoras Navieras Ahorradas
            </span>
            <span className="text-base sm:text-lg font-black text-blue-400">
              ${metricasTurno.demoras} <span className="text-[10px] text-slate-400">USD</span>
            </span>
          </div>

          <div className="p-3 rounded-2xl bg-slate-900/80 border border-purple-500/30">
            <span className="text-[10px] text-slate-400 font-bold uppercase block flex items-center gap-1.5">
              <FileCheck2 className="h-3.5 w-3.5 text-purple-400" />
              Auditoría SOP de Turno
            </span>
            <span className="text-base sm:text-lg font-black text-purple-300">
              {metricasTurno.completados}/{metricasTurno.totalItems}{' '}
              <span className="text-xs text-purple-400">({metricasTurno.precision}%)</span>
            </span>
          </div>

          <div className="p-3 rounded-2xl bg-slate-900/80 border border-amber-500/30">
            <span className="text-[10px] text-slate-400 font-bold uppercase block flex items-center gap-1.5">
              <Award className="h-3.5 w-3.5 text-amber-400" />
              Nivel de Competencia
            </span>
            <span className="text-xs font-bold text-amber-300 truncate block mt-1">
              {metricasTurno.nivelCompetencia}
            </span>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* LÍNEA DE TIEMPO INTERACTIVA / STEPPER DE 5 ESTACIONES */}
        {/* ========================================================================= */}
        <div className="mt-4 pt-4 border-t border-slate-800/80 flex sm:grid sm:grid-cols-5 gap-2 font-mono text-xs overflow-x-auto no-scrollbar touch-pan-x pb-1">
          {[
            { n: 1, label: '1. Revalidación', sub: 'Naviera BL', icono: Ship },
            { n: 2, label: '2. Báscula BP1', sub: 'Pesaje SOLAS', icono: Scale },
            { n: 3, label: '3. Previo Ocular', sub: 'Patio & Sellos', icono: Search },
            { n: 4, label: '4. Semáforo SAT', sub: 'Modulación ANAM', icono: Radio },
            { n: 5, label: '5. Tarja & Salida', sub: 'Gate-Out / Entrega', icono: CheckCircle2 }
          ].map((estacion) => {
            const Icono = estacion.icono
            const completada = etapa > estacion.n
            const activa = etapa === estacion.n

            return (
              <button
                key={estacion.n}
                type="button"
                onClick={() => setEtapa(estacion.n as EtapaSimulacion)}
                className={`p-2.5 sm:p-3 rounded-2xl border text-left transition-all relative shrink-0 min-w-[135px] sm:min-w-0 flex-1 sm:flex-initial ${
                  activa
                    ? 'bg-cyan-500/10 border-cyan-500/40 shadow-lg shadow-cyan-500/10 scale-[1.02]'
                    : completada
                    ? 'bg-slate-900/90 border-emerald-500/30 text-emerald-400'
                    : 'bg-slate-900/50 border-slate-800 text-slate-500 hover:border-slate-700'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <Icono
                    className={`h-4 w-4 ${
                      activa ? 'text-cyan-400' : completada ? 'text-emerald-400' : 'text-slate-600'
                    }`}
                  />
                  {completada ? (
                    <span className="h-4 w-4 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-black flex items-center justify-center">
                      ✓
                    </span>
                  ) : activa ? (
                    <span className="h-2 w-2 rounded-full bg-cyan-400 animate-pulse" />
                  ) : null}
                </div>
                <p className={`font-bold text-xs truncate ${activa ? 'text-white' : ''}`}>
                  {estacion.label}
                </p>
                <p className="text-[10px] text-slate-500 truncate">{estacion.sub}</p>
              </button>
            )
          })}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* GAFETE OPERATIVO DEL PUESTO ACTIVO (IDENTIDAD LABORAL) */}
      {/* ========================================================================= */}
      <div className={`p-4 rounded-2xl bg-gradient-to-r ${puestoLaboral.color} border ${puestoLaboral.borde} text-white shadow-lg flex flex-col sm:flex-row sm:items-center justify-between gap-4`}>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/20">
            <Briefcase className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-black/30 font-mono tracking-wider">
                PUESTO DE TRABAJO ASIGNADO
              </span>
              <span className="text-[10px] font-bold text-white/80">
                {puestoLaboral.entidad}
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/20 text-white border border-white/30 font-mono">
                👤 Responsable: {etapa === 1
                  ? (expediente.actores.emisor.alumnoNombre || expediente.actores.emisor.nombre)
                  : etapa === 2 || etapa === 3
                  ? (expediente.actores.agenteAduanal.alumnoNombre || 'Inspector Portuario')
                  : etapa === 4
                  ? (expediente.actores.agenteAduanal.alumnoNombre || 'Auditor ANAM / SAT')
                  : (expediente.actores.receptor.alumnoNombre || expediente.actores.receptor.nombre)}
              </span>
            </div>
            <h3 className="text-base font-black tracking-tight mt-0.5">
              {puestoLaboral.titulo}
            </h3>
            <p className="text-xs text-white/90 leading-tight">
              {puestoLaboral.mision}
            </p>
          </div>
        </div>

        <div className="shrink-0 bg-black/30 px-3.5 py-2 rounded-xl border border-white/10 text-center font-mono">
          <span className="text-[9px] uppercase tracking-widest text-white/70 block">GAFETE OFICIAL</span>
          <span className="text-xs font-black tracking-wider text-cyan-300">{puestoLaboral.gafete}</span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* ESCENARIO ACTIVO DE LA SIMULACIÓN + CHECKLIST SOP Y BITÁCORA */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* PANEL PRINCIPAL DEL ESCENARIO (8 COLUMNAS) */}
        <div className="lg:col-span-8 space-y-6">
          {/* ===================================================================== */}
          {/* ESTACIÓN 1: REVALIDACIÓN NAVIERA */}
          {/* ===================================================================== */}
          {etapa === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-xl space-y-5"
            >
              <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    <Ship className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-white">
                      Estación 1: Revalidación de Documento Marítimo ante Naviera
                    </h3>
                    <p className="text-xs text-slate-400">
                      Línea Naviera: <strong>{expediente.contenedor.lineaNaviera}</strong> · Buque: {expediente.buque.nombre}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => onAbrirHojaFisica('BL')}
                  className="px-3 py-1.5 rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 text-xs font-bold hover:bg-cyan-500/20 transition-all flex items-center gap-1.5"
                >
                  <FileText className="h-3.5 w-3.5" />
                  <span>Auditar B/L en Hoja Física</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-1">
                  <span className="text-[10px] font-bold uppercase text-slate-500 block">Número de B/L:</span>
                  <span className="font-mono font-bold text-sm text-cyan-400">
                    {expediente.documentos.bl.numero}
                  </span>
                  <span className="text-[10px] text-slate-400 block">Tipo: B/L Master (BL M)</span>
                </div>

                <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-1">
                  <span className="text-[10px] font-bold uppercase text-slate-500 block">Consignee (Receptor):</span>
                  <span className="font-bold text-xs text-white truncate block">
                    {expediente.actores.receptor.nombre}
                  </span>
                  <span className="text-[10px] text-purple-400 font-mono">
                    RFC: {expediente.actores.receptor.rfc}
                  </span>
                </div>

                <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase text-slate-500 block">Estatus Naviero:</span>
                    <button
                      type="button"
                      onClick={() => {
                        const nuevoEstado = !blRevalidado
                        setBlRevalidado(nuevoEstado)
                        if (nuevoEstado) {
                          agregarLog('NAVIERA', `B/L ${expediente.documentos.bl.numero} revalidado con éxito tras pago de derechos navieros.`, 'SUCCESS')
                          toast.success('B/L revalidado correctamente')
                        } else {
                          agregarLog('NAVIERA', `B/L ${expediente.documentos.bl.numero} retenido por falta de liberación naviera.`, 'WARN')
                          toast('B/L retenido / pendiente', { icon: '⚠️' })
                        }
                      }}
                      className="text-[10px] font-mono text-cyan-400 hover:underline"
                    >
                      {blRevalidado ? 'Simular Retención' : 'Revalidar B/L'}
                    </button>
                  </div>
                  <span
                    className={`font-black text-xs inline-block px-2 py-0.5 rounded ${
                      blRevalidado ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
                    }`}
                  >
                    {blRevalidado ? '✓ B/L REVALIDADO' : '✗ PENDIENTE DE REVALIDAR'}
                  </span>
                  <span className="text-[10px] text-slate-400 block">Flete y Demoras: $20.00 USD OK</span>
                </div>
              </div>

              {/* Botón de Acción para Avanzar */}
              <div className="flex justify-end pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    if (!blRevalidado) {
                      toast.error('No se puede ingresar a Garita Portuaria sin B/L Revalidado por la Naviera.')
                      agregarLog('GARITA PIS', 'Acceso denegado: B/L sin liberación naviera (Falta Revalidación).', 'DANGER')
                      return
                    }
                    avanzarEtapa(2)
                  }}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-all flex items-center gap-2"
                >
                  <span>B/L Autorizado ➔ Avanzar a Garita & Báscula BP1</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* ===================================================================== */}
          {/* ESTACIÓN 2: GARITA PIS & BÁSCULA BP1 (PESAJE ELECTRÓNICO) */}
          {/* ===================================================================== */}
          {etapa === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-xl space-y-5"
            >
              <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    <Scale className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-white">
                      Estación 2: Garita PIS & Báscula Plataforma 1 (Servicio BP1)
                    </h3>
                    <p className="text-xs text-slate-400">
                      Pesaje certificado conforme al Convenio Internacional SOLAS (Regla 2, Cap. VI).
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onAbrirHojaFisica('PIS')}
                    className="px-3 py-1.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs font-bold hover:bg-emerald-500/20 transition-all flex items-center gap-1.5"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    <span>Ver Pase PIS</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => onAbrirHojaFisica('VGM')}
                    className="px-3 py-1.5 rounded-xl border border-blue-500/30 bg-blue-500/10 text-blue-400 text-xs font-bold hover:bg-blue-500/20 transition-all flex items-center gap-1.5"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    <span>Certificado VGM</span>
                  </button>
                </div>
              </div>

              {/* Display LED de la Báscula */}
              <div className="p-6 rounded-3xl bg-black border-2 border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-inner">
                <div className="space-y-1 text-center sm:text-left">
                  <span className="text-[10px] font-mono font-bold tracking-widest uppercase text-emerald-500 flex items-center gap-2 justify-center sm:justify-start">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    BÁSCULA FISCAL BP-01 · MANZANILLO RECINTO
                  </span>
                  <p className="text-xs text-slate-400 font-mono">
                    Placas: {expediente.actores.transportista.placas} · Chofer: {expediente.actores.transportista.chofer}
                  </p>
                </div>

                {/* Display Digital de Números Grandes */}
                <div className="px-6 py-3 rounded-2xl bg-emerald-950/40 border border-emerald-500/40 text-center font-mono">
                  <span className="text-[10px] text-emerald-400 font-bold block uppercase tracking-widest">
                    PESO BRUTO VEHICULAR
                  </span>
                  <span className="text-3xl sm:text-4xl font-black text-emerald-400 tracking-wider">
                    {pesoDisplay > 0 ? pesoDisplay.toLocaleString() : '00,000'}{' '}
                    <span className="text-sm text-emerald-600">KG</span>
                  </span>
                </div>

                {/* Botón de Ejecutar Pesaje */}
                <button
                  type="button"
                  disabled={simulando || pesajeCompletado}
                  onClick={handleSimularPesaje}
                  className="px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-emerald-500/25 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
                >
                  <Play className="h-4 w-4" />
                  <span>{pesajeCompletado ? 'Pesaje Concluido' : simulando ? 'Pesando...' : 'Iniciar Pesaje BP1'}</span>
                </button>
              </div>

              {/* Indicador de Tolerancia SOLAS */}
              {pesajeCompletado && (
                <div
                  className={`p-4 rounded-2xl border flex items-center justify-between ${
                    toleranciaOk
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                      : 'bg-rose-500/10 border-rose-500/30 text-rose-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {toleranciaOk ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-rose-400 shrink-0" />
                    )}
                    <div>
                      <p className="text-xs font-bold">
                        {toleranciaOk
                          ? 'VGM Validado: Cumple tolerancia SOLAS de ±500 kg.'
                          : 'Alerta SOLAS: Sobrepeso detectado excede los 500 kg de tolerancia máxima.'}
                      </p>
                      <p className="text-[11px] opacity-80 mt-0.5 font-mono">
                        VGM en Báscula: {(pesoDisplay - 13600).toLocaleString()} kg · VGM Declarado:{' '}
                        {expediente.contenedor.vgmDeclaradoKg.toLocaleString()} kg
                      </p>
                    </div>
                  </div>

                  <span className="font-mono text-xs font-black uppercase px-2.5 py-1 rounded bg-black/40">
                    {toleranciaOk ? 'APROBADO' : 'RETENIDO'}
                  </span>
                </div>
              )}

              {/* Botón para Avanzar */}
              <div className="flex justify-between pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setEtapa(1)}
                  className="px-4 py-2 rounded-xl border border-slate-700 text-xs font-bold text-slate-400 hover:text-white"
                >
                  Regresar
                </button>

                <button
                  type="button"
                  disabled={!pesajeCompletado}
                  onClick={() => avanzarEtapa(3)}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  <span>Báscula BP1 OK ➔ Avanzar a Previo Ocular en Patio</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* ===================================================================== */}
          {/* ESTACIÓN 3: PATIO DE MANIOBRAS & EXAMEN PREVIO */}
          {/* ===================================================================== */}
          {etapa === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-xl space-y-5"
            >
              <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-400 border border-purple-500/20">
                    <Search className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-white">
                      Estación 3: Patio de Maniobras SSA & Examen Previo Ocular
                    </h3>
                    <p className="text-xs text-slate-400">
                      Ubicación asignada en patio: <strong>B04-18-02-3</strong> · Inspección C-TPAT de 7 puntos.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => onAbrirHojaFisica('TARJA')}
                  className="px-3 py-1.5 rounded-xl border border-purple-500/30 bg-purple-500/10 text-purple-400 text-xs font-bold hover:bg-purple-500/20 transition-all flex items-center gap-1.5"
                >
                  <FileText className="h-3.5 w-3.5" />
                  <span>Auditar Tarja SSA</span>
                </button>
              </div>

              {/* Simulación del Contenedor e Inspección de Sellos */}
              <div className="p-5 rounded-2xl bg-slate-950/70 border border-slate-800 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase text-slate-500 block">Contenedor en Inspección:</span>
                  <span className="font-mono text-base font-black text-white">
                    {expediente.contenedor.numero}
                  </span>
                  <span className="text-[10px] text-slate-400 block">{expediente.contenedor.tipo} · {expediente.contenedor.lineaNaviera}</span>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase text-slate-500 block">Sellos Fiscales Verificados:</span>
                  <div className="flex items-center gap-2">
                    {selloVerificado ? (
                      <Lock className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <Unlock className="h-4 w-4 text-rose-400" />
                    )}
                    <span className="font-mono text-xs font-bold text-white">
                      {expediente.contenedor.sellos}
                    </span>
                  </div>
                  <span className="text-[10px] text-emerald-400 block">Precinto de Alta Seguridad ISO 17712</span>
                </div>

                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setPrevioRealizado(true)
                      agregarLog(
                        'AGENTE ADUANAL',
                        `Examen previo completado en patio TEC I. Bultos contados: ${expediente.documentos.packingList.bultos}. Etiquetado comercial conforme.`,
                        'SUCCESS'
                      )
                      toast.success('Examen Previo Ocular concluido con éxito.')
                    }}
                    className="w-full py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-black text-xs uppercase tracking-wider shadow-md shadow-purple-500/25 transition-all flex items-center justify-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    <span>Realizar Previo Ocular</span>
                  </button>
                </div>
              </div>

              {/* Botón para Avanzar */}
              <div className="flex justify-between pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setEtapa(2)}
                  className="px-4 py-2 rounded-xl border border-slate-700 text-xs font-bold text-slate-400 hover:text-white"
                >
                  Regresar
                </button>

                <button
                  type="button"
                  disabled={!previoRealizado}
                  onClick={() => avanzarEtapa(4)}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  <span>Previo Conforme ➔ Presentar al Semáforo Fiscal SAT</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* ===================================================================== */}
          {/* ESTACIÓN 4: SEMÁFORO FISCAL SAT (SELECCIÓN AUTOMATIZADA) */}
          {/* ===================================================================== */}
          {etapa === 4 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-xl space-y-6"
            >
              <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20">
                    <Radio className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-white">
                      Estación 4: Mecanismo de Selección Automatizada (Semáforo SAT)
                    </h3>
                    <p className="text-xs text-slate-400">
                      Aduana de Manzanillo (160) · Presentación del Pedimento de Comercio Exterior.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onAbrirHojaFisica('DODA')}
                    className="px-3 py-1.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs font-bold hover:bg-emerald-500/20 transition-all flex items-center gap-1.5"
                  >
                    <Radio className="h-3.5 w-3.5" />
                    <span>Ver DODA SAT con QR</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => onAbrirHojaFisica('PEDIMENTO')}
                    className="px-3 py-1.5 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-400 text-xs font-bold hover:bg-amber-500/20 transition-all flex items-center gap-1.5"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    <span>Pedimento Oficial</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => onAbrirHojaFisica('MVE')}
                    className="px-3 py-1.5 rounded-xl border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 text-xs font-bold hover:bg-indigo-500/20 transition-all flex items-center gap-1.5"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    <span>MVE (Art. 81)</span>
                  </button>
                </div>
              </div>

              {/* Gabinete de Luces del Semáforo Fiscal */}
              <div className="p-8 rounded-3xl bg-black border-2 border-slate-800 flex flex-col sm:flex-row items-center justify-center gap-8 shadow-inner">
                <div className="p-4 rounded-3xl bg-slate-950 border-4 border-slate-800 flex flex-col items-center gap-4 shadow-2xl">
                  {/* Luz Roja */}
                  <div
                    className={`h-14 w-14 rounded-full border-4 border-slate-900 transition-all flex items-center justify-center ${
                      resultadoSemaforo === 'ROJO'
                        ? 'bg-rose-500 shadow-[0_0_35px_rgba(244,63,94,0.9)] animate-pulse'
                        : 'bg-rose-950/40 opacity-40'
                    }`}
                  >
                    <span className="text-[10px] font-black text-white">ROJO</span>
                  </div>

                  {/* Luz Verde */}
                  <div
                    className={`h-14 w-14 rounded-full border-4 border-slate-900 transition-all flex items-center justify-center ${
                      resultadoSemaforo === 'VERDE'
                        ? 'bg-emerald-500 shadow-[0_0_35px_rgba(16,185,129,0.9)] animate-pulse'
                        : 'bg-emerald-950/40 opacity-40'
                    }`}
                  >
                    <span className="text-[10px] font-black text-white">VERDE</span>
                  </div>
                </div>

                {/* Explicación y Botón Pulsador */}
                <div className="space-y-3 text-center sm:text-left max-w-sm">
                  <h4 className="text-sm font-black text-white uppercase tracking-wider font-mono">
                    Mecanismo de Selección Automatizada (Art. 43 L.A.)
                  </h4>
                  <p className="text-xs text-slate-400">
                    Al pasar el código de barras del pedimento, el sistema del SAT determina aleatoriamente si la mercancía obtiene desaduanamiento libre o requiere inspección intrusiva.
                  </p>

                  <button
                    type="button"
                    disabled={semaforoPulsado}
                    onClick={handlePulsarSemaforo}
                    className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg shadow-amber-500/25 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
                  >
                    <Zap className="h-4 w-4" />
                    <span>{semaforoPulsado ? 'Dictamen Determinado' : 'Pulsar Botón de Selección SAT'}</span>
                  </button>
                </div>
              </div>

              {/* Botón para Avanzar */}
              <div className="flex justify-between pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setEtapa(3)}
                  className="px-4 py-2 rounded-xl border border-slate-700 text-xs font-bold text-slate-400 hover:text-white"
                >
                  Regresar
                </button>

                <button
                  type="button"
                  disabled={!resultadoSemaforo}
                  onClick={() => avanzarEtapa(5)}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  <span>Proceder a Garita de Salida (Gate-Out)</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* ===================================================================== */}
          {/* ESTACIÓN 5: GATE-OUT & FIRMA DE TARJA DE INTERNACIÓN */}
          {/* ===================================================================== */}
          {etapa === 5 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-xl space-y-5"
            >
              <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-teal-500/10 text-teal-400 border border-teal-500/20">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-white">
                      Estación 5: Garita de Salida (Gate-Out) & Conclusión del Despacho
                    </h3>
                    <p className="text-xs text-slate-400">
                      Entrega al alumno consignatario / receptor y firma de la Tarja de Internación.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const nuevo = !tarjaFirmada
                      setTarjaFirmada(nuevo)
                      if (nuevo) {
                        agregarLog('SSA MEXICO', 'Tarja de Internación firmada y validada por el receptor.', 'SUCCESS')
                        toast.success('Tarja firmada electrónicamente')
                      } else {
                        agregarLog('SSA MEXICO', 'Firma de tarja revertida a pendiente.', 'WARN')
                      }
                    }}
                    className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition-all flex items-center gap-1.5 ${
                      tarjaFirmada
                        ? 'border-emerald-500/40 bg-emerald-500/20 text-emerald-300'
                        : 'border-slate-700 bg-slate-800 text-slate-300 hover:text-white'
                    }`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    <span>{tarjaFirmada ? 'Tarja Firmada' : 'Firmar Tarja'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => onAbrirHojaFisica('TARJA')}
                    className="px-3 py-1.5 rounded-xl border border-teal-500/30 bg-teal-500/10 text-teal-400 text-xs font-bold hover:bg-teal-500/20 transition-all flex items-center gap-1.5"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    <span>Ver Tarja SSA</span>
                  </button>
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-white uppercase">Resumen de la Simulación Operativa:</span>
                  <span className="text-xs font-mono font-bold text-emerald-400">100% COMPLETADO</span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono">
                  <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
                    <span className="text-[10px] text-slate-500 block">REVALIDACIÓN:</span>
                    <span className="font-bold text-emerald-400">✓ EXITOSA</span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
                    <span className="text-[10px] text-slate-500 block">BÁSCULA BP1:</span>
                    <span className="font-bold text-emerald-400">✓ CUMPLE SOLAS</span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
                    <span className="text-[10px] text-slate-500 block">EXAMEN PREVIO:</span>
                    <span className="font-bold text-emerald-400">✓ CONFORME</span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
                    <span className="text-[10px] text-slate-500 block">SEMÁFORO SAT:</span>
                    <span
                      className={`font-black ${
                        resultadoSemaforo === 'VERDE' ? 'text-emerald-400' : 'text-rose-400'
                      }`}
                    >
                      {resultadoSemaforo || 'VERDE'}
                    </span>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between text-xs font-mono">
                  <span className="text-slate-400">Estatus Firma de Tarja:</span>
                  <span className={tarjaFirmada ? 'text-emerald-400 font-bold' : 'text-amber-400 font-bold'}>
                    {tarjaFirmada ? '✓ FIRMADA POR ALUMNO CONFORME' : '⏳ PENDIENTE DE FIRMAR'}
                  </span>
                </div>
              </div>

              {/* Botón de Cierre y Dictamen Final */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setEtapa(4)}
                  className="w-full sm:w-auto px-4 py-2 rounded-xl border border-slate-700 text-xs font-bold text-slate-400 hover:text-white"
                >
                  Regresar
                </button>

                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => setModalDictamenAbierto(true)}
                    className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 font-bold text-xs uppercase tracking-wider hover:bg-cyan-500/20 transition-all flex items-center justify-center gap-2"
                  >
                    <Award className="h-4 w-4" />
                    <span>Ver Dictamen de Competencia Laboral</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleFinalizarDespacho}
                    className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg shadow-emerald-500/25 transition-all flex items-center justify-center gap-2"
                  >
                    <Check className="h-4 w-4" />
                    <span>Concluir Turno y Firmar Tarja</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ===================================================================== */}
          {/* TABLILLA DE AUDITORÍA SOP OFICIAL DE LA ESTACIÓN (CHECKLIST INTERACTIVO) */}
          {/* ===================================================================== */}
          <div className="p-5 rounded-3xl bg-slate-900/60 border border-slate-800/80 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800/80">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-cyan-400" />
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-white font-mono">
                    Procedimiento Operativo Estándar (SOP) · Verificación de Puesto
                  </h4>
                  <p className="text-[10px] text-slate-400">
                    Como operador real, debes validar estos 3 controles obligatorios antes de autorizar el paso:
                  </p>
                </div>
              </div>

              <span className="text-[10px] font-mono font-bold text-cyan-400 px-2 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/20">
                PUESTO {etapa} DE 5
              </span>
            </div>

            <div className="space-y-3">
              {REGLAS_SOP.filter((r) => r.estacion === etapa).map((item) => {
                const realizado = checksRealizados.includes(item.id)

                return (
                  <div
                    key={item.id}
                    onClick={() => handleToggleCheck(item)}
                    className={`p-3.5 rounded-2xl border transition-all cursor-pointer flex items-start gap-3 ${
                      realizado
                        ? 'bg-emerald-500/10 border-emerald-500/30'
                        : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <div
                      className={`h-5 w-5 rounded-lg border mt-0.5 flex items-center justify-center shrink-0 transition-all ${
                        realizado
                          ? 'bg-emerald-500 border-emerald-400 text-slate-950'
                          : 'border-slate-700 bg-slate-900'
                      }`}
                    >
                      {realizado && <Check className="h-3.5 w-3.5 stroke-[3]" />}
                    </div>

                    <div className="space-y-1 flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className={`text-xs font-bold ${realizado ? 'text-emerald-300' : 'text-white'}`}>
                          {item.titulo}
                        </span>
                        <div className="flex items-center gap-1.5 font-mono text-[10px]">
                          {item.multaEvitadaMXN > 0 && (
                            <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-bold border border-emerald-500/20">
                              +${item.multaEvitadaMXN.toLocaleString()} MXN Multa Evitada
                            </span>
                          )}
                          {item.demoraEvitadaUSD > 0 && (
                            <span className="px-2 py-0.5 rounded bg-blue-500/15 text-blue-400 font-bold border border-blue-500/20">
                              +${item.demoraEvitadaUSD} USD Demora Ahorrada
                            </span>
                          )}
                        </div>
                      </div>

                      <p className="text-[11px] text-slate-400 leading-snug">
                        {item.detalle}
                      </p>

                      <p className="text-[10px] font-mono text-cyan-400/90 pt-1 flex items-center gap-1">
                        <HelpCircle className="h-3 w-3 shrink-0" />
                        <span>Fundamento Legal: <strong>{item.fundamento}</strong></span>
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* PANEL LATERAL DERECHO: BITÁCORA EN TIEMPO REAL (TOS LIVE EVENT LOG) */}
        {/* ========================================================================= */}
        <div className="lg:col-span-4 space-y-5">
          <div className="p-5 rounded-3xl bg-[#0c0d12] border border-slate-800 shadow-xl space-y-3 flex flex-col h-[520px]">
            <div className="flex items-center justify-between pb-2 border-b border-slate-800/80">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-cyan-400 animate-ping" />
                <h4 className="text-xs font-black uppercase tracking-wider text-white font-mono">
                  TOS Telemetría & Log Portuario
                </h4>
              </div>
              <span className="text-[9px] font-mono text-slate-500">TIDEWORKS / PIS</span>
            </div>

            {/* Lista de Eventos con scroll */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1 text-[11px] font-mono no-scrollbar">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="p-2.5 rounded-xl bg-slate-900/60 border border-slate-800/60 space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-[9px] font-black px-1.5 py-0.5 rounded ${
                        log.tipo === 'SUCCESS'
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : log.tipo === 'DANGER'
                          ? 'bg-rose-500/10 text-rose-400'
                          : log.tipo === 'WARN'
                          ? 'bg-amber-500/10 text-amber-400'
                          : 'bg-blue-500/10 text-cyan-400'
                      }`}
                    >
                      {log.modulo}
                    </span>
                    <span className="text-[9px] text-slate-500">{log.hora}</span>
                  </div>
                  <p className="text-slate-300 leading-tight text-[10px]">{log.mensaje}</p>
                </div>
              ))}
            </div>

            {/* Accesos Rápidos a Hojas Físicas Oficiales */}
            <div className="pt-3 border-t border-slate-800 space-y-1.5">
              <span className="text-[10px] font-bold uppercase text-slate-400 block">
                Dossier Físico en Papel:
              </span>
              <div className="grid grid-cols-2 gap-1.5 text-[10px] font-bold">
                <button
                  type="button"
                  onClick={() => onAbrirHojaFisica('PEDIMENTO')}
                  className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 hover:border-amber-500/40 text-slate-300 hover:text-amber-400 transition-all text-left truncate flex items-center gap-1.5"
                >
                  <FileText className="h-3 w-3 text-amber-500 shrink-0" />
                  <span>Pedimento SAT</span>
                </button>

                <button
                  type="button"
                  onClick={() => onAbrirHojaFisica('BL')}
                  className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 hover:border-cyan-500/40 text-slate-300 hover:text-cyan-400 transition-all text-left truncate flex items-center gap-1.5"
                >
                  <Ship className="h-3 w-3 text-cyan-500 shrink-0" />
                  <span>B/L Marítimo</span>
                </button>

                <button
                  type="button"
                  onClick={() => onAbrirHojaFisica('TARJA')}
                  className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 hover:border-purple-500/40 text-slate-300 hover:text-purple-400 transition-all text-left truncate flex items-center gap-1.5"
                >
                  <Layers className="h-3 w-3 text-purple-500 shrink-0" />
                  <span>Tarja SSA</span>
                </button>

                <button
                  type="button"
                  onClick={() => onAbrirHojaFisica('VGM')}
                  className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 hover:border-blue-500/40 text-slate-300 hover:text-blue-400 transition-all text-left truncate flex items-center gap-1.5"
                >
                  <Scale className="h-3 w-3 text-blue-500 shrink-0" />
                  <span>VGM SOLAS</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* MODAL DICTAMEN DE COMPETENCIA LABORAL Y EVALUACIÓN DE DESEMPEÑO (PORTAL AL BODY) */}
      {/* ========================================================================= */}
      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {modalDictamenAbierto && (
              <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-md w-screen h-screen">
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl border border-cyan-500/40 bg-[#0d0e14] p-6 sm:p-8 shadow-2xl text-white font-sans space-y-6"
                >
                  {/* Encabezado del Dictamen */}
                  <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25">
                        <Award className="h-6 w-6" />
                      </div>
                      <div>
                        <span className="text-[10px] font-mono font-black text-cyan-400 uppercase tracking-widest block">
                          EVALUACIÓN DE PRÁCTICA PROFESIONAL · SIMULADOR 1.0
                        </span>
                        <h3 className="text-xl font-black text-white">
                          Dictamen de Competencia Laboral Portuaria
                        </h3>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setModalDictamenAbierto(false)}
                      className="text-slate-400 hover:text-white p-1 rounded-lg"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Ficha del Dictamen */}
                  <div className="p-5 rounded-2xl bg-slate-950/80 border border-slate-800 space-y-4 font-mono text-xs">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-3">
                      <div>
                        <span className="text-[10px] text-slate-500 block uppercase">EMBARQUE AUDITADO:</span>
                        <span className="font-bold text-cyan-400 text-sm">{expediente.folio}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-500 block uppercase">CONTENEDOR:</span>
                        <span className="font-bold text-white text-sm">{expediente.contenedor.numero}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-500 block uppercase">TERMINAL:</span>
                        <span className="font-bold text-white text-sm">SSA Manzanillo</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800">
                        <span className="text-[10px] text-slate-400 block">PRECISIÓN SOP:</span>
                        <span className="text-xl font-black text-emerald-400">{metricasTurno.precision}%</span>
                      </div>
                      <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800">
                        <span className="text-[10px] text-slate-400 block">MULTAS EVITADAS:</span>
                        <span className="text-base font-black text-emerald-400">
                          ${metricasTurno.multas.toLocaleString()} MXN
                        </span>
                      </div>
                      <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800 col-span-2 sm:col-span-1">
                        <span className="text-[10px] text-slate-400 block">DEMORAS AHORRADAS:</span>
                        <span className="text-base font-black text-blue-400">
                          ${metricasTurno.demoras} USD
                        </span>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 space-y-1">
                      <span className="text-[10px] font-bold uppercase text-emerald-400 block">
                        DICTAMEN FINAL DEL CONSEJO TÉCNICO:
                      </span>
                      <p className="font-sans font-bold text-sm text-white">
                        {metricasTurno.precision >= 80
                          ? '✓ APROBADO CON EXCELENCIA OPERATIVA · Nivel de Preparación Apto para Puesto Real en Manzanillo'
                          : metricasTurno.precision >= 50
                          ? '⚠️ APROBADO REGULAR · Requiere reforzar auditoría de precintos C-TPAT y Convenio SOLAS'
                          : '✗ NO ACREDITADO · Omisión crítica de procedimientos obligatorios del SAT / Terminal'}
                      </p>
                      <p className="font-sans text-xs text-slate-300 pt-1">
                        El alumno demostró dominio sobre los 5 puestos operativos del puerto en el Simulador 1.0, ejecutando la revalidación naviera, verificación del pesaje VGM, examen previo ocular, modulación ante el SAT y firma legal de la tarja.
                      </p>
                    </div>
                  </div>

                  {/* Acciones del Modal */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-800">
                    <button
                      type="button"
                      onClick={() => {
                        window.print()
                      }}
                      className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-300 font-bold text-xs uppercase flex items-center justify-center gap-2"
                    >
                      <Printer className="h-4 w-4" />
                      <span>Imprimir Dictamen / Evidencia</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setModalDictamenAbierto(false)}
                      className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg shadow-cyan-500/25"
                    >
                      Continuar
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  )
}

export default SimuladorDespachoEnVivo
